// @ts-nocheck
/**
 * CarrierIdentities: the people mobile lines are registered to (2026-10-08).
 * Settings > Numbers shows one card per person with their lines.
 *
 * The operator sells French mobile lines to persons only, and at most 2 per
 * verified identity. So a line is always ordered in someone's name: this file
 * lists those people with their verification state and lines used, and adds
 * one (identity fields plus the identity document, front and back). The
 * document goes to the operator through the server; Revcall keeps none.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api/api-client';
import { errorMessage, errorToastOptions } from '@/services/errorMessage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export interface CarrierIdentity {
  id: string;
  first_name: string;
  last_name: string;
  status: 'verified' | 'checking' | 'action_required';
  lines_used: number;
  max_lines: number;
}

export function useCarrierIdentities(enabled: boolean) {
  return useQuery({
    queryKey: ['carrier-identities'],
    queryFn: async () => {
      const { data } = await api.functions.invoke('listCarrierIdentities', {});
      return (data?.identities || []) as CarrierIdentity[];
    },
    enabled,
  });
}

/** A line can be ordered in this person's name. */
export function canTakeLine(i: CarrierIdentity): boolean {
  return i.status === 'verified' && i.lines_used < i.max_lines;
}

/** One short reason why a person cannot take a line, or '' when they can. */
export function blockedReason(i: CarrierIdentity, t: (k: string, o?: object) => string): string {
  if (i.status === 'checking') return t('settings:identities.reasonChecking');
  if (i.status === 'action_required') return t('settings:identities.reasonAction');
  if (i.lines_used >= i.max_lines) return t('settings:identities.reasonFull', { max: i.max_lines });
  return '';
}

const STATUS_STYLE = {
  verified: { background: 'var(--vf-green-bg)', color: 'var(--vf-green)' },
  checking: { background: 'var(--vf-yellow-bg)', color: 'var(--vf-yellow)' },
  action_required: { background: 'var(--vf-red-bg)', color: 'var(--vf-red)' },
};

/** Whether the operator verified the person; never clickable. */
export function IdentityStatusBadge({ status }: { status: CarrierIdentity['status'] }) {
  const { t } = useTranslation(['settings']);
  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded" style={STATUS_STYLE[status]}>
      {t(`settings:identities.status.${status}`)}
    </span>
  );
}

const EMPTY = {
  salutation: 'MR', first_name: '', last_name: '', email: '', phone: '',
  type_of_id: 'NATIONAL_ID_CARD', id_number: '', building_number: '', street: '', zip_code: '', city: '',
};

/** Adds a person: their identity, their address in France, their document. */
export function AddIdentityDialog({ open, onOpenChange, onCreated }: { open: boolean; onOpenChange: (v: boolean) => void; onCreated?: (id: string) => void }) {
  const { t } = useTranslation(['settings', 'common']);
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const set = (k: string) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const complete = Object.entries(form).every(([, v]) => String(v).trim() !== '') && files.length > 0;

  const submit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      for (const [k, v] of Object.entries(form)) fd.append(k, String(v).trim());
      fd.append('nationality', 'FR');
      for (const f of files) fd.append('documents', f, f.name);
      const { data } = await api.functions.invoke('createCarrierIdentity', { form: fd });
      const status = data?.identity?.status;
      toast.success(t(`settings:identities.created.${status || 'checking'}`));
      queryClient.invalidateQueries({ queryKey: ['carrier-identities'] });
      if (data?.identity?.id) onCreated?.(data.identity.id);
      setForm(EMPTY);
      setFiles([]);
      onOpenChange(false);
    } catch (err) {
      toast.error(errorMessage(err, t, 'settings:identities.createFailed'), errorToastOptions(err));
    } finally {
      setBusy(false);
    }
  };

  const field = (k: string, labelKey: string, props = {}) => (
    <div>
      <Label className="text-xs">{t(`settings:identities.fields.${labelKey}`)}</Label>
      <Input className="h-9 mt-1" value={form[k]} onChange={set(k)} {...props} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-foreground">{t('settings:identities.dialogTitle')}</DialogTitle>
          <DialogDescription>{t('settings:identities.dialogWhy')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">{t('settings:identities.fields.salutation')}</Label>
              <Select value={form.salutation} onValueChange={(v) => setForm((f) => ({ ...f, salutation: v }))}>
                <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="MR">{t('settings:identities.mr')}</SelectItem>
                  <SelectItem value="MS">{t('settings:identities.ms')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {field('first_name', 'firstName', { autoComplete: 'given-name' })}
            {field('last_name', 'lastName', { autoComplete: 'family-name' })}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {field('email', 'email', { type: 'email', autoComplete: 'email' })}
            {field('phone', 'phone', { type: 'tel', autoComplete: 'tel', placeholder: '+33612345678' })}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">{t('settings:identities.fields.typeOfId')}</Label>
              <Select value={form.type_of_id} onValueChange={(v) => setForm((f) => ({ ...f, type_of_id: v }))}>
                <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NATIONAL_ID_CARD">{t('settings:identities.idCard')}</SelectItem>
                  <SelectItem value="PASSPORT">{t('settings:identities.passport')}</SelectItem>
                  <SelectItem value="RESIDENCE_PERMIT">{t('settings:identities.residencePermit')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {field('id_number', 'idNumber')}
          </div>
          <div className="grid grid-cols-4 gap-2">
            {field('building_number', 'buildingNumber')}
            <div className="col-span-3">{field('street', 'street', { autoComplete: 'address-line1' })}</div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {field('zip_code', 'zipCode', { autoComplete: 'postal-code' })}
            <div className="col-span-2">{field('city', 'city', { autoComplete: 'address-level2' })}</div>
          </div>
          <div>
            <Label className="text-xs">{t('settings:identities.fields.documents')}</Label>
            <Input
              type="file"
              multiple
              accept="image/*,application/pdf"
              className="h-9 mt-1 text-xs"
              onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 3))}
            />
            <p className="text-xs text-muted-foreground mt-1">{t('settings:identities.documentsHint')}</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" className="h-9" disabled={busy} onClick={() => onOpenChange(false)}>
            {t('common:actions.cancel')}
          </Button>
          <Button size="sm" className="h-9" disabled={!complete || busy} onClick={() => void submit()}>
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
            {t('settings:identities.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Forgets a person with no line left. The server refuses while the operator
 * still counts one, so the button only shows when none is counted. The
 * operator's API cannot delete their documents: the dialog says so.
 */
export function RemoveIdentityButton({ identity }: { identity: CarrierIdentity }) {
  const { t } = useTranslation(['settings', 'common']);
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (identity.lines_used > 0) return null;
  const name = `${identity.first_name} ${identity.last_name}`;

  const remove = async () => {
    setBusy(true);
    try {
      await api.functions.invoke('removeCarrierIdentity', { id: identity.id });
      toast.success(t('settings:identities.removed'));
      queryClient.invalidateQueries({ queryKey: ['carrier-identities'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-numbers'] });
      setOpen(false);
    } catch (err) {
      toast.error(errorMessage(err, t, 'settings:identities.removeFailed'), errorToastOptions(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setOpen(true)}>
        {t('settings:identities.remove')}
      </button>
      <Dialog open={open} onOpenChange={(v) => { if (!busy) setOpen(v); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground">{t('settings:identities.removeTitle', { name })}</DialogTitle>
            <DialogDescription>{t('settings:identities.removeWhy')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" size="sm" className="h-9" disabled={busy} onClick={() => setOpen(false)}>
              {t('common:actions.cancel')}
            </Button>
            <Button variant="destructive" size="sm" className="h-9" disabled={busy} onClick={() => void remove()}>
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
              {t('settings:identities.removeConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
